import React, { useEffect, useCallback, useState} from 'react';
import EffectsComponent from 'components/effects/EffectsComponent.js';
import {connect} from 'react-redux';
import {effectOptions, getEffectOption, initializeEffect} from 'components/effects/constants.js';

const EffectRackDocumentation = (props) => { 
    let [effect, setEffect] = useState(undefined);
    useEffect(() => {
        let track = props.sequencerEngine.tracks[0];
        initializeEffect("Effect Rack", props.sequencerEngine);
        let effect = initializeEffect("Effect Rack", props.sequencerEngine);
        effect.enable(true);
        if (effect.wetParameter) {
            effect.wetParameter.set(1);
        }
        track.effectsChain.addEffect(effect);
        setEffect(effect);

        return () => {
            // remove the effect rack
            track.effectsChain.deleteEffect(effect);
        };
    }, [setEffect]);

    return (
        <div className="effect-rack-documentation">
          <p>
            Layering effects in series-- one after the other-- can sometimes feel
            limiting, since you can only change the order of effects.
          </p>
          <p>
            The <b>Effect Rack</b> allows you to route effects in more complex ways. It’s
            available under the “Audio FX” dropdown. When you add an Effect Rack to
            your effects chain, you’ll see this:
          </p>
          <p>
            {effect ?
             <EffectsComponent.EffectComponent
               sequencerEngine={props.sequencerEngine}
               onDeleteEffect={() => []}
               effect={effect}
               track={props.sequencerEngine.tracks[0]}/> : ''}
          </p>
 
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Nodes
              </p>
              <p>
                An effect rack consists of an <b>input node</b> (representing the signal coming into
                the effect rack), an <b>output node</b>  (representing the signal coming out of the
                effect rack), and any number of <b>effect nodes</b> between. 
              </p>
              <p>
                You can connect nodes together, passing the audio signal from the one node
                to another. The arrow connecting two nodes is called an <b>edge</b>.
              </p>
              <p>
                <b>Note:</b> In order to hear anything there needs to be a connection, or group
                connections from the input node to the output node.
              </p>
              <p className="sub-header">
                Adding Effect Node
              </p>
              <p>
                To add an effect node in an Effect Rack, go through “Add FX”
                dropdown and select an effect. When added it will not be connected to any nodes.
              </p>
              <p>
                You will need to connect the effect to the effect rack, on order to hear it.
              </p>
              <p className="sub-header">
                Connecting Effects
              </p>
              <p>
                To connect two nodes together, hover above the <b>output for a node</b> and start
                dragging from there with your mouse. You’ll notice an arrow will appear.
                Drag your mouse all the way to the <b>input for a different node</b>. 
              </p>
              <p>
                When you release the mouse, there will be a new edge between the two nodes.
                The first node is now feeding audio signal into the other node.
              </p>
              <p className="sub-header">
                Parameter Modulation
              </p>
              <p>
                Effect Rack’s allow you to intuitively modulate any effect’s parameters
                by either the LFOs or Envelopes.
              </p>
              <p>
                Each of the LFOs and Envelopes live as nodes at the top of the effect rack in a row.
              </p>
              <p>
                You can connect an LFO or Envelope to an effect node’s parameter, in the
                same way you connect two nodes together. By dragging the circle below
                the LFO or Envelope node all the way to the destination node parameter’s circle.
              </p>
              <p className="sub-header">
                Macros
              </p>
              <p>
                Effect Rack’s can quickly become complex, with several effects each containing several parameters. 
              </p>
              <p>
                A <b>macro</b> lets you control several parameters at once, instead of
                having to change each parameter individually. 
              </p>
              <p>
                To create a macro, just click the <b>New Macro button</b>. The new macro will
                appear in the Effect Rack. Connect the output of the newly created
                macro to the parameters you wish to control with this macro.
              </p>
              <p className="sub-header">
                Midi Effect
              </p>
              <p>
                The <b>Midi Effect Rack</b> midi effect takes the effect rack concept and
                allows you to route midi effects in the same way. 
              </p>
              <p>
                The <b>edge</b> in a <b>Midi Effect Rack</b> represents a trigger passing from one
                effect to another. Clicking on the edge brings up the <b>edge properties</b>
                for that <b>edge</b>, allowing you to edit the probablity that a trigger will
                pass through that <b>edge</b>, on a given step.
              </p>
            </div>
            <div className="text-column">
              <p className="sub-header">
                Anatomy of Node
              </p>
              <p>
                <img src="/AnatomyNode.png"/>
                <div>1. Input for a node</div>
                <div>1. Output for a node</div>
                <div>3-5. Parameters for a node</div>
              </p>
              <p>
                <b>Note: </b> Hover over the circles in the node to view
                a tooltip describing what it is.
              </p>
              <p>
                <img src="/AnatomyEdge.png"/>
              </p>
              <p className="sub-header">
                Selecting Nodes and Edges
              </p>
              <p>
                Click on a node or edge to select it. When selected, they will
                turn red and the area to the right of the effect rack will show the
                properties of the selected node or edge.
              </p>
              <p>
                For <b>nodes</b>, the selected effect will show up-- allowing you edit
                the parameters, as if it were a normal effect.
              </p>
              <p>
                For <b>edges</b>, the selected edge will show up, allowing you to edge the
                gain (volume) of the singal being fed through the edge. The stronger the
                signal, the brighter the edge will get.
              </p>
              <p>
                Pressing the <b>delete</b> key on your keyboard will delete the
                selected node or edge.
              </p>
              <p>
                Typing <b>CMD+d</b> or <b>Ctrl+d</b> will add a duplicated copy of the
                selected effect node to the Effect Rack.
              </p>
              <p className="sub-header">
                Working with Multiple Nodes
              </p>
              <p>
                You can drag you mouse to select several nodes. Once selected you can
                move them together, copy and paste them, and delete them
              </p>
              <p>
                When multiple nodes are selected, a <b>Connect Nodes</b> button will
                appear at the top of the Effect Rack. Clicking this will connect
                the nodes in series-- one after another.
              </p>
            </div>
          </div>
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let track = props.sequencerEngine.tracks && props.sequencerEngine.tracks[0];
    if (track) {
        return {
            numEffects: track.effects.length
        };
    }
    return {
        numEffects: 0
    };
};

const mapDispatchToProps = (dispatch) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EffectRackDocumentation);
